/* eslint-disable max-lines */
/**
 * Caption editor top chrome (#2231).
 *
 * Uses the shared `EditorHeader`. Owns the same rename / delete /
 * media-source modals as the transcript editor and mirrors its action
 * ordering so both editors expose identical chrome.
 */

import { useEffect, useMemo, useState } from 'react';
import { useCaption } from '@utils/providers/caption-context.tsx';
import { SessionDatesTooltip } from '@components/editors/shared/session-dates-tooltip.tsx';
import { formatDuration, safeToLocaleTimeString } from '@utils/date.ts';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import cn from 'classnames';
import { useDeleteTranscriptSession } from '@utils/api/sessions/use-delete-transcript-session.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import toast from 'react-hot-toast';
import { Params, useNavigate, useParams } from 'react-router-dom';
import { useUpdateDocument } from '@utils/api/documents/use-update-document.tsx';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base.ts';
import {
  AlertCircle,
  Archive,
  Bot,
  Check,
  ChevronDown,
  Download,
  FileText,
  Globe,
  Keyboard,
  Link,
  Loader2,
  PenLine,
  Redo2,
  RotateCcw,
  Save,
  Search,
  Send,
  Timer,
  TimerReset,
  Trash2,
  Undo2
} from 'lucide-react';
import Input from '@components/atoms/ui-kit/input.tsx';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal.tsx';
import MediaSourceModal from '@components/editors/media-source-modal.tsx';
import { Switch } from '@headlessui/react';
import { useOrganization } from '@utils/api/users/use-get-organization.tsx';
import { PrimaryColor } from '@utils/constants/style.ts';
import { useGetDocumentTranslations } from '@utils/api/documents/use-get-document-translations.ts';
import {
  useOptimisticFinished,
  useSwallowDocumentFinished
} from '@utils/helpers/document-finished.ts';
import { CaptionExportDocument } from '@components/editors/caption-editor/toolbar/caption-export-document.tsx';
import { TranscriptSummarizeButton } from '@components/editors/transcript-editor/transcript-summarize-button.tsx';
import { CommentsHeaderControls } from '@components/editors/transcript-editor/transcript-comments-gutter.tsx';
import {
  useGetEntitlements,
  getEntitlementsWithFallback
} from '@utils/api/entitlements/use-get-entitlements.tsx';
import { SessionDocument } from '@utils/api/documents/use-get-session-documents.ts';
import { getReturnFolder } from '@utils/editors/return-to-folder.ts';
import { ChangeLanguageModal } from '@components/editors/shared/change-language-modal/change-language-modal.tsx';
import { useTranslationQueue } from '@components/editors/shared/change-language-modal/use-translation-queue.ts';
import { useTranslationQuote } from '@utils/api/documents/use-translation-quote.ts';
import { useCaptionHotkeys } from '@utils/providers/caption-hotkey-context.tsx';
import { useCaptionEditorContext } from '@utils/providers/caption-editor-context.tsx';
import KeyboardShortcutsModal, {
  captionShortcutGroups
} from '@components/editors/keyboard-shortcuts-modal.tsx';
import CaptionTimecode from '@components/editors/caption-editor/toolbar/caption-timecode.tsx';
import { useAudio } from '@utils/providers/audio-context.tsx';
import { PATHS } from '../../../router/paths.ts';
import EditorHeader, {
  EditorHeaderAction
} from '@components/editors/shared/editor-header.tsx';
import PublishHistoryPanel from '@components/publishing/publish-history-panel.tsx';
import PublishLatestInline from '@components/publishing/publish-latest-inline.tsx';
import { useIsPublishingEnabled } from '@utils/api/connections/use-is-publishing-enabled.tsx';
import { History } from 'lucide-react';

type Props = {
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  hasTranslations?: boolean;
  translatedDocuments?: SessionDocument[];
  selectedTranslationId?: string | null;
  onTranslationChange?: (docId: string) => void;
  /**
   * Overrides the toolbar save-button handler. In split view the parent
   * passes a dual-save that fans out to both the source and the
   * translation pane; without it the translation pane's edits never reach
   * the server because the source's `saveDocument` doesn't see them.
   */
  onSave?: (userAction?: boolean) => void;
  /**
   * Override the header Undo / Redo. In split view the translation pane
   * has its own document history that this component can't reach, so the
   * parent routes the buttons to it.
   */
  onUndo?: () => void;
  onRedo?: () => void;
};

const CaptionAbout = ({
  enabled,
  setEnabled,
  translatedDocuments: splitTranslatedDocs,
  selectedTranslationId,
  onTranslationChange,
  onSave,
  onUndo: onUndoOverride,
  onRedo: onRedoOverride
}: Props) => {
  useSwallowDocumentFinished();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getColour } = useThemeContext();
  const { sessionId, documentId } = useParams<Params>();
  const { timecodeOffset } = useAudio();
  const {
    documentResult: document,
    session,
    document: captionDocument,
    saveStatus,
    lastLocalSave,
    lastServerSync,
    updateDocumentResult,
    finishDocument,
    finishedLoading,
    saveDocument,
    saveDocumentLoading,
    documentResetting,
    cleanDocument,
    cleanDocumentLoading,
    undo: undoDoc,
    redo: redoDoc,
    reset
  } = useCaption();
  const {
    undo: undoText,
    redo: redoText,
    isAnyEditorFocused,
    canUndoText,
    canRedoText
  } = useCaptionEditorContext();
  const {
    openShortcutsModal,
    shortcutsModalOpen,
    closeShortcutsModal,
    openSearch
  } = useCaptionHotkeys();

  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [filename, setFilename] = useState<string>(
    document?.result?.filename || session?.filename || ''
  );
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [cleanOpen, setCleanOpen] = useState(false);
  const [timecodeOpen, setTimecodeOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isCleaning, setIsCleaning] = useState(false);
  const [isMediaSourceOpen, setIsMediaSourceOpen] = useState(false);
  const [isSummarizeOpen, setIsSummarizeOpen] = useState(false);
  // Lifted for the overflow menu's narrow-width fallback (#2231).
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isPublishOpen, setIsPublishOpen] = useState(false);
  const [publishHistoryOpen, setPublishHistoryOpen] = useState(false);
  const { isEnabled: isPublishingEnabled } = useIsPublishingEnabled();
  const canSeePublishHistory = isPublishingEnabled;
  const canSeePublish = isPublishingEnabled;
  const [languageModalMode, setLanguageModalMode] = useState<
    'navigate' | 'right-panel' | null
  >(null);
  const [isToggling, setIsToggling] = useState(false);
  const { mutate: deleteSession } = useDeleteTranscriptSession();
  const { mutate: updateSession } = useUpdateDocument();
  const { data: organization } = useOrganization();
  const { data: entitlementsData } = useGetEntitlements();
  const entitlements = getEntitlementsWithFallback(entitlementsData);
  const aiSummariesEnabled = entitlements.features.ai_summaries;
  const captionIsEmpty = !captionDocument?.document?.length;
  const currentRevisionId = document?.result?.current_revision_id ?? null;
  const { data: translationsData } = useGetDocumentTranslations(
    session?.session_id,
    documentId
  );

  const translationSource = translationsData?.result?.source;
  const translatedDocuments = translationsData?.result?.translations ?? [];

  // #2241: new-translation queue — see transcript-about for the full
  // rationale. Shared hook so both editors get the same behaviour.
  const translationQueue = useTranslationQueue({
    sessionId: session?.session_id ?? '',
    documentId: translationSource?.id ?? documentId ?? '',
    filename: translationSource?.filename ?? null,
    completedLanguages: translatedDocuments.map(d => d.language),
    currentDocumentId: documentId
  });
  const {
    data: translationQuote,
    isFetching: quoteLoading,
    isError: quoteError,
    refetch: refetchQuote
  } = useTranslationQuote(
    session?.session_id,
    translationSource?.id ?? documentId,
    languageModalMode !== null
  );
  const currentLanguage = (
    document?.result?.language || session?.language
  )?.toUpperCase();

  const { uiFinished, setUiFinished, serverFinished } = useOptimisticFinished(
    enabled,
    document,
    session
  );

  const hasOffset = Number(timecodeOffset) > 0;

  const handleToggle = async (nextStatus: boolean) => {
    if (!sessionId || !documentId) return;
    if (isToggling || finishedLoading) return;
    if (nextStatus === uiFinished && nextStatus === serverFinished) return;

    setIsToggling(true);
    const prevUI = uiFinished;
    setUiFinished(nextStatus);
    setEnabled(nextStatus);

    try {
      await new Promise<void>((resolve, reject) => {
        try {
          finishDocument(
            sessionId,
            documentId,
            nextStatus,
            (finished: boolean, err?: unknown) => {
              if (err) return reject(err);
              setUiFinished(Boolean(finished));
              setEnabled(Boolean(finished));
              resolve();
            }
          );
        } catch (e) {
          reject(e);
        }
      });
    } catch (e: unknown) {
      const error = e as {
        response?: {
          status?: number;
          data?: { error?: string; error_description?: string };
        };
        message?: string;
      };
      const code = error?.response?.status;
      const errKey = error?.response?.data?.error;
      if (code === 400 && errKey === 'document-finished') {
        setUiFinished(true);
        setEnabled(true);
        toast(
          t('document-already-finalized') || 'Document is already finalized.'
        );
      } else {
        setUiFinished(prevUI);
        setEnabled(prevUI);
        const msg =
          error?.response?.data?.error_description ||
          error?.message ||
          t('an-error-occurred');
        toast.error(msg);
      }
    } finally {
      setIsToggling(false);
    }
  };

  const statusInfo = useMemo(() => {
    switch (saveStatus) {
      case 'synced':
        return {
          text: t('status.synced'),
          color: 'text-green-600',
          bgColor: 'bg-green-100',
          icon: <Check className="inline-block w-3.5 h-3.5" />,
          timestamp: lastServerSync
            ? safeToLocaleTimeString(lastServerSync)
            : null
        };
      case 'saved-locally':
        return {
          text: t('status.saved-locally'),
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-100',
          icon: <Save className="inline-block w-3.5 h-3.5" />,
          timestamp: lastLocalSave
            ? safeToLocaleTimeString(lastLocalSave)
            : null
        };
      case 'saving':
        return {
          text: t('status.saving'),
          color: 'text-blue-600',
          bgColor: 'bg-blue-100',
          icon: <Loader2 className="inline-block w-3.5 h-3.5 animate-spin" />,
          timestamp: null
        };
      case 'unsaved':
      default:
        return {
          text: t('status.unsaved'),
          color: 'text-red-600',
          bgColor: 'bg-red-100',
          icon: <AlertCircle className="inline-block w-3.5 h-3.5" />,
          timestamp: null
        };
    }
  }, [saveStatus, lastLocalSave, lastServerSync, t]);

  useEffect(() => {
    setFilename(document?.result?.filename || session?.filename || '');
  }, [document, session]);

  const handleDelete = async () => {
    if (!sessionId || !document?.result.id) return;
    setIsDeleting(true);
    await new Promise(resolve => setTimeout(resolve, 300));
    deleteSession(
      { sessionId, documentId: document.result.id },
      {
        onSuccess: () => {
          toast.success(t('document-deleted-successfully'));
          setIsDeleting(false);
          setDeleteOpen(false);
          navigate(PATHS().HOME);
        },
        onError: (error: AxiosError<BaseError>) => {
          const data = error?.response?.data;
          let errorMessage = t('delete-document-failed');
          if (data?.error_description) errorMessage = data.error_description;
          else if (data?.detail) {
            if (Array.isArray(data.detail)) {
              errorMessage = (data.detail as Array<{ msg: string }>)
                .map(e => e.msg)
                .join(', ');
            } else if (typeof data.detail === 'string')
              errorMessage = data.detail;
          } else if (data?.error) errorMessage = data.error;
          else if (data?.message) errorMessage = data.message;
          toast.error(errorMessage);
          setIsDeleting(false);
        }
      }
    );
  };

  const handleFilenameChange = async () => {
    if (!sessionId || !documentId) return;
    setIsUpdating(true);
    updateSession(
      { filename, sessionId, documentId },
      {
        onSuccess: () => {
          if (document) {
            updateDocumentResult({
              ...document,
              result: { ...document.result, filename }
            });
          }
          toast.success(t('document-name-updated-successfully'));
          setIsEditModalOpen(false);
          setIsUpdating(false);
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          const data = error?.response?.data;
          let errorMessage = t('failed-to-update-filename');
          if (data?.error_description) errorMessage = data.error_description;
          else if (data?.detail) {
            if (Array.isArray(data.detail)) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              errorMessage = data.detail.map((e: any) => e.msg).join(', ');
            } else if (typeof data.detail === 'string')
              errorMessage = data.detail;
          } else if (data?.error) errorMessage = data.error;
          else if (data?.message) errorMessage = data.message;
          toast.error(errorMessage);
          setIsUpdating(false);
        }
      }
    );
  };

  const handleReset = async () => {
    setIsResetting(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    reset();
    setIsResetting(false);
    setResetOpen(false);
  };

  const handleClean = async () => {
    setIsCleaning(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    cleanDocument();
    setIsCleaning(false);
    setCleanOpen(false);
  };

  const onUndo = () => {
    if (onUndoOverride) return onUndoOverride();
    if (isAnyEditorFocused && canUndoText) undoText();
    else undoDoc();
  };
  const onRedo = () => {
    if (onRedoOverride) return onRedoOverride();
    if (isAnyEditorFocused && canRedoText) redoText();
    else redoDoc();
  };

  const handleBack = () => {
    const returnFolderId = getReturnFolder(documentId);
    navigate(
      returnFolderId ? PATHS(returnFolderId).FOLDER_CONTENT : PATHS().HOME
    );
  };

  const liveCharacterCount =
    captionDocument?.document?.reduce(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (acc: number, seg: any) => acc + (seg?.text?.length ?? 0),
      0
    ) ?? 0;

  const subtitle = (
    // `flex-nowrap overflow-hidden` on the row + `whitespace-nowrap
    // shrink-0` on each child so phone widths clip trailing metadata
    // instead of stacking it onto a second row (#2231 narrow-width AC).
    <div className="flex items-center gap-3 flex-nowrap overflow-hidden min-w-0">
      {session?.duration && (
        <span className="flex items-center gap-1 tabular-nums whitespace-nowrap shrink-0">
          <Timer size={12} style={{ color: getColour('icon') }} aria-hidden />
          {formatDuration(session.duration)}
        </span>
      )}
      <span className="flex items-center gap-1 tabular-nums">
        <FileText size={12} style={{ color: getColour('icon') }} aria-hidden />
        {liveCharacterCount} {t('characters').toLowerCase()}
      </span>
      {currentLanguage && (
        <span className="flex items-center gap-1 uppercase tracking-wider whitespace-nowrap shrink-0">
          <Globe size={12} style={{ color: getColour('icon') }} aria-hidden />
          {onTranslationChange ? (
            <button
              type="button"
              onClick={() => setLanguageModalMode('navigate')}
              className="inline-flex items-center gap-1 hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
              style={{ color: getColour('inactiveText') }}
              aria-label={t('change-language')}
            >
              {currentLanguage}
              <ChevronDown size={10} aria-hidden />
            </button>
          ) : (
            <span style={{ color: getColour('inactiveText') }}>
              {currentLanguage}
            </span>
          )}
        </span>
      )}
      <span
        className={cn(
          'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap shrink-0',
          statusInfo.color,
          statusInfo.bgColor
        )}
        title={
          statusInfo.timestamp
            ? `${t('status.last-saved')}: ${statusInfo.timestamp}`
            : statusInfo.text
        }
      >
        {statusInfo.icon}
        <span>{statusInfo.text}</span>
      </span>
      <SessionDatesTooltip
        dateUpload={session?.date_upload}
        dateSessionStart={session?.date_session_start}
        dateFinished={session?.date_finished}
      />
      {canSeePublishHistory && sessionId && documentId && (
        <PublishLatestInline
          sessionId={sessionId}
          documentId={documentId}
          onOpenHistory={() => setPublishHistoryOpen(true)}
        />
      )}
    </div>
  );

  const actions: EditorHeaderAction[] = [
    ...(canSeePublishHistory && sessionId && documentId
      ? [
          {
            key: 'publish-history',
            label: t('publish-history-title'),
            icon: <History size={14} />,
            onClick: () => setPublishHistoryOpen(true)
          } as EditorHeaderAction
        ]
      : []),
    {
      key: 'kbd-shortcuts',
      label: t('keyboard-shortcuts'),
      icon: <Keyboard size={14} />,
      onClick: openShortcutsModal,
      shortcut: 'Shift+?'
    },
    {
      key: 'undo',
      label: t('undo'),
      icon: <Undo2 size={14} />,
      onClick: onUndo,
      disabled: saveDocumentLoading,
      shortcut: 'Ctrl+Z',
      // #2289: pinned so the user can reach undo/redo/save in one click
      // instead of opening the overflow menu for every edit cycle.
      pinned: true
    },
    {
      key: 'redo',
      label: t('redo'),
      icon: <Redo2 size={14} />,
      onClick: onRedo,
      disabled: saveDocumentLoading,
      shortcut: 'Ctrl+Y',
      pinned: true
    },
    {
      key: 'save',
      label: t('save'),
      icon: <Save size={14} />,
      onClick: () => (onSave ?? saveDocument)(true),
      disabled: saveDocumentLoading || enabled,
      loading: saveDocumentLoading,
      shortcut: 'Ctrl+S',
      pinned: true
    },
    {
      key: 'search',
      label: t('search-caption'),
      icon: <Search size={14} />,
      onClick: openSearch,
      shortcut: 'Ctrl+F',
      pinned: true
    },
    // Translation picker — matches the transcript editor:
    // one pill that opens the ChangeLanguageModal. Picking a
    // translation sets `?lang=XX` → split view; picking Original
    // clears the param → single pane. No separate split-view toggle.
    ...(onTranslationChange
      ? [
          {
            key: 'translation-picker',
            label: t('right-panel-language'),
            icon: <Globe size={14} />,
            pinned: true,
            estimatedWidth: 132,
            render: () => (
              <button
                type="button"
                onClick={() => setLanguageModalMode('right-panel')}
                // #2289: match the h-9 of the surrounding header buttons.
                className="inline-flex items-center gap-1.5 px-3 h-9 rounded-2xl text-xs font-medium hover:opacity-80 border focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                style={{
                  backgroundColor: PrimaryColor + '15',
                  color: PrimaryColor,
                  borderColor: PrimaryColor + '40'
                }}
                aria-label={t('right-panel-language')}
              >
                <Globe size={12} aria-hidden />
                {(splitTranslatedDocs ?? [])
                  .find(d => d.id === selectedTranslationId)
                  ?.language?.toUpperCase() || t('original')}
                <ChevronDown size={10} aria-hidden />
              </button>
            )
          } as EditorHeaderAction
        ]
      : []),
    {
      key: 'finalize',
      label: uiFinished ? t('finalized') : t('editable'),
      icon: <Check size={14} />,
      // #2289: see transcript-about for rationale — the finished/editable
      // toggle lives in the overflow dropdown now; undo/redo/save are the
      // header's new pinned trio.
      render: ({ inOverflow }) => {
        const toggle = (
          <Switch
            checked={uiFinished}
            onChange={handleToggle}
            className={cn(
              'group relative inline-flex h-4 w-8 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              (finishedLoading || isToggling) && 'opacity-50 cursor-not-allowed'
            )}
            disabled={finishedLoading || isToggling}
          >
            <span className="sr-only">{t('use-setting')}</span>
            <span
              aria-hidden="true"
              className="pointer-events-none absolute mx-auto h-3 w-6 rounded-full transition-colors duration-200 ease-in-out"
              style={{
                backgroundColor: uiFinished
                  ? organization?.reseller_branding?.brand_color || PrimaryColor
                  : getColour('border')
              }}
            />
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-0 inline-block h-4 w-4 transform rounded-full shadow ring-0 transition-transform duration-200 ease-in-out group-data-[checked]:translate-x-3"
              style={{
                backgroundColor: getColour('firstLayer'),
                border: `1px solid ${getColour('border')}`
              }}
            />
          </Switch>
        );
        if (inOverflow) {
          return (
            <label
              className="flex w-full items-center justify-between gap-3 px-3 py-2 text-sm cursor-pointer"
              style={{ color: getColour('normalText') }}
            >
              <span className="flex items-center gap-3">
                <span
                  aria-hidden="true"
                  className="inline-flex h-4 w-4 items-center justify-center"
                  style={{ color: getColour('icon') }}
                >
                  <Check size={14} />
                </span>
                {uiFinished ? t('finalized') : t('editable')}
              </span>
              {toggle}
            </label>
          );
        }
        return (
          <label
            className="inline-flex items-center gap-2 px-2 h-9 rounded-2xl border"
            style={{
              borderColor: getColour('border'),
              backgroundColor: getColour('secondLayer')
            }}
          >
            <span
              className="text-[11px] font-medium leading-none"
              style={{ color: getColour('normalText') }}
            >
              {uiFinished ? t('finalized') : t('editable')}
            </span>
            {toggle}
          </label>
        );
      }
    },
    {
      key: 'timecode',
      label: t('timecode') + (hasOffset ? ' •' : ''),
      icon: <TimerReset size={14} />,
      onClick: () => setTimecodeOpen(true),
      overflowOnly: true
    },
    {
      key: 'clean',
      label: t('clean-up'),
      icon: <Archive size={14} />,
      onClick: () => setCleanOpen(true),
      disabled:
        saveDocumentLoading || cleanDocumentLoading || documentResetting,
      loading: cleanDocumentLoading,
      overflowOnly: true
    },
    {
      key: 'edit-name',
      label: t('edit-name'),
      icon: <PenLine size={14} />,
      onClick: () => setIsEditModalOpen(true),
      overflowOnly: true
    },
    // Narrow-width fallback for the trailing Export CTA. The trailing
    // cluster is hidden below md (#2231) so these overflow entries
    // keep Export + AI Summary reachable from the "…" menu.
    {
      key: 'export',
      label: t('export'),
      icon: <Download size={14} />,
      onClick: () => setIsExportOpen(true),
      overflowOnly: true
    },
    ...(canSeePublish && sessionId && documentId
      ? [
          {
            key: 'publish-send',
            label: t('publish-cta'),
            icon: <Send size={14} />,
            onClick: () => setIsPublishOpen(true),
            overflowOnly: true
          } as EditorHeaderAction
        ]
      : []),
    ...(aiSummariesEnabled
      ? [
          {
            key: 'ai-summary',
            label: t('ai-summary'),
            icon: <Bot size={14} />,
            onClick: () => setIsSummarizeOpen(true),
            disabled: captionIsEmpty,
            overflowOnly: true
          } as EditorHeaderAction
        ]
      : []),
    {
      key: 'media-source',
      label: t('media-source'),
      icon: <Link size={14} />,
      onClick: () => setIsMediaSourceOpen(true),
      overflowOnly: true
    },
    {
      key: 'reset',
      label: t('reset'),
      icon: <RotateCcw size={14} />,
      onClick: () => setResetOpen(true),
      disabled: saveDocumentLoading || documentResetting || enabled,
      overflowOnly: true
    },
    {
      key: 'delete',
      label: t('delete'),
      icon: <Trash2 size={14} />,
      variant: 'danger',
      onClick: () => setDeleteOpen(true),
      overflowOnly: true
    }
  ];

  return (
    <>
      <EditorHeader
        onBack={handleBack}
        title={document?.result?.filename || session?.filename || ''}
        subtitle={subtitle}
        status={
          sessionId && documentId ? (
            <CommentsHeaderControls
              sessionId={sessionId}
              documentId={documentId}
              translationDocumentId={selectedTranslationId}
            />
          ) : undefined
        }
        actions={actions}
        trailing={
          <>
            <CaptionExportDocument
              downloadOpen={isExportOpen}
              onDownloadOpenChange={setIsExportOpen}
              publishOpen={isPublishOpen}
              onPublishOpenChange={setIsPublishOpen}
            />
            {aiSummariesEnabled && (
              <TranscriptSummarizeButton
                documentId={documentId ?? ''}
                disabled={captionIsEmpty}
                quota={entitlements.ai_summary_quota}
                currentRevisionId={currentRevisionId}
                lastServerSync={lastServerSync}
                renderTrigger={false}
                open={isSummarizeOpen}
                onOpenChange={setIsSummarizeOpen}
              />
            )}
          </>
        }
      />

      <KeyboardShortcutsModal
        isOpen={shortcutsModalOpen}
        onClose={closeShortcutsModal}
        groups={captionShortcutGroups(t)}
      />

      <ConfirmationModal
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        size="small"
        title={t('delete-confirmation')}
        caption={t('delete-document-confirmation')}
        confirmLabel={isDeleting ? t('deleting') : t('delete')}
        cancelLabel={t('cancel')}
        confirmDisabled={isDeleting}
        destructive
        autoFocusConfirm
      />

      <ConfirmationModal
        isOpen={resetOpen}
        onClose={() => setResetOpen(false)}
        onConfirm={handleReset}
        size="small"
        title={t('reset-confirmation')}
        caption={t('reset-confirmation-desc')}
        confirmLabel={isResetting ? t('reseting') : t('reset')}
        cancelLabel={t('cancel')}
        confirmDisabled={isResetting}
        destructive={false}
        autoFocusConfirm
      />

      <ConfirmationModal
        isOpen={cleanOpen}
        onClose={() => setCleanOpen(false)}
        onConfirm={handleClean}
        size="small"
        title={t('cleanup-confirmation')}
        caption={t('cleanup-confirmation-desc')}
        confirmLabel={isCleaning ? t('cleaning') : t('clean')}
        cancelLabel={t('cancel')}
        confirmDisabled={isCleaning}
        destructive={false}
        autoFocusConfirm
      />

      {timecodeOpen && <CaptionTimecode setTimeCodeOpen={setTimecodeOpen} />}

      {isMediaSourceOpen && sessionId && (
        <MediaSourceModal
          isOpen={isMediaSourceOpen}
          onClose={() => setIsMediaSourceOpen(false)}
          sessionId={sessionId}
          currentMediaUrl={session?.media_url}
          session={session}
        />
      )}

      {isEditModalOpen && (
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => !isUpdating && setIsEditModalOpen(false)}
          size="small"
          title={
            t('edit-document-name') +
            ' ' +
            (document?.result?.filename || session?.filename)
          }
          caption={t('')}
          footerActions={[
            <Button
              onClick={() => setIsEditModalOpen(false)}
              label={t('cancel')}
              size="small"
              variant="smoke"
              disabled={isUpdating}
            />,
            <Button
              onClick={handleFilenameChange}
              label={isUpdating ? t('updating') : t('save')}
              size="small"
              variant="primary"
              disabled={isUpdating}
            />
          ]}
        >
          <div className="py-2">
            <form className="mt-1 sm:items-center min-w-fit">
              <div className="min-w-72">
                <Input
                  id="filename"
                  label={t('filename')}
                  defaultValue={document?.result?.filename || session?.filename}
                  value={filename}
                  onChange={e => {
                    const value = e.target.value;
                    if (value.length <= 200) setFilename(value);
                  }}
                  type="text"
                  placeholder={t('filename')}
                  disabled={isUpdating}
                />
              </div>
            </form>
          </div>
        </Modal>
      )}

      {languageModalMode !== null && session?.session_id && (
        <ChangeLanguageModal
          isOpen={true}
          onClose={() => setLanguageModalMode(null)}
          sessionId={session.session_id}
          quote={translationQuote?.result}
          quoteLoading={quoteLoading}
          quoteError={quoteError}
          onRetryQuote={() => void refetchQuote()}
          inProgressCodes={translationQueue.inProgressCodes}
          onStartTranslation={translationQueue.start}
          source={
            translationSource
              ? {
                  id: translationSource.id,
                  language: translationSource.language,
                  filename: translationSource.filename
                }
              : null
          }
          translations={translatedDocuments.map(d => ({
            id: d.id,
            language: d.language,
            filename: d.filename
          }))}
          activeDocId={
            languageModalMode === 'right-panel'
              ? (selectedTranslationId ?? '')
              : (documentId ?? '')
          }
          // The doc the user opened via the URL. In right-panel mode
          // this is the row that collapses split view when clicked.
          primaryDocId={documentId ?? ''}
          onSelectDoc={docId => {
            // Picking the current primary doc never navigates — the
            // user is already there. We only need to drop the `?lang=`
            // query so split view collapses. Without this branch,
            // `navigate(samePath)` is a React Router no-op and the
            // query lingers (user bug: "clicking Original does
            // nothing").
            const isCurrentPrimary = docId === documentId;
            if (languageModalMode === 'right-panel' || isCurrentPrimary) {
              onTranslationChange?.(docId);
            } else if (session.session_id) {
              navigate(PATHS(session.session_id, docId).CAPTIONS);
            }
            setLanguageModalMode(null);
          }}
        />
      )}
      {canSeePublishHistory && sessionId && documentId && (
        <Modal
          isOpen={publishHistoryOpen}
          onClose={() => setPublishHistoryOpen(false)}
          size="medium"
          title={t('publish-history-title')}
          caption={t('publish-history-subtitle')}
          footerActions={[
            <Button
              key="close"
              label={t('close')}
              size="small"
              variant="smoke"
              onClick={() => setPublishHistoryOpen(false)}
            />
          ]}
        >
          <PublishHistoryPanel sessionId={sessionId} documentId={documentId} />
        </Modal>
      )}
    </>
  );
};

export default CaptionAbout;
